import { useState, useEffect } from 'react';
import { X, Download, Share, Plus, Smartphone, ExternalLink } from 'lucide-react';
import { openInNativeApp } from '../utils/openInNativeApp';

import { useTranslation } from 'react-i18next';

// App Store / Play Store URLs for the published native apps.
const APP_STORE_URL = 'https://apps.apple.com/us/app/eili-bible-study-tool/id6761586127';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.askeili.android';
// `market://` opens the Play Store app directly (no Chrome bounce) if
// installed; falls back to the https URL otherwise.
const PLAY_STORE_INTENT = 'market://details?id=com.askeili.android';

// Detect iOS/iPadOS — covers iPad in desktop-class Safari which reports
// MacIntel + maxTouchPoints > 1.
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Detect Android (mobile or tablet WebView/Chrome).
const isAndroid = () => /android/i.test(navigator.userAgent);

// True if the current page is running inside a native wrapper — Despia
// (iOS), TWA (Play Store via Digital Goods API), or any custom WebView
// that injects an `EiliNativeBridge`. Always true while in-app.
const isInsideNativeWrapper = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('despia')) return true;
  if ('getDigitalGoodsService' in window) return true; // TWA marker
  if (window.EiliNativeBridge || window.NativePaymentBridge) return true;
  if (document.referrer.startsWith('android-app://')) return true;
  return false;
};

// Already-installed PWA (standalone display mode or iOS Safari standalone).
const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
};

// Per-channel dismiss keys — dismissing the App Store banner doesn't
// dismiss the desktop PWA install banner and vice versa, because they
// solve different problems.
const DISMISS_KEYS = {
  appStore: 'eili_install_appstore_dismissed',
  playStore: 'eili_install_playstore_dismissed',
  pwa: 'eili_install_dismissed', // legacy key for PWA — kept stable
};

export function InstallPrompt() {
  const {
    t
  } = useTranslation();

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showStoreBanner, setShowStoreBanner] = useState(null); // 'ios' | 'android' | null
  const [showIOSAddToHome, setShowIOSAddToHome] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Hide entirely if already in-app or installed.
    if (isInsideNativeWrapper() || isStandalone()) return;

    // Mobile path: route to the matching native store first.
    if (isIOS()) {
      // Apple ships a native Smart App Banner via the apple-itunes-app
      // meta tag in index.html that auto-renders on Safari iOS. We
      // *also* show our own banner so non-Safari iOS browsers (Chrome
      // for iOS, Firefox for iOS) get a CTA, and so users on Safari
      // who dismissed Apple's banner still see ours.
      if (localStorage.getItem(DISMISS_KEYS.appStore)) return;
      const timer = setTimeout(() => setShowStoreBanner('ios'), 3000);
      return () => clearTimeout(timer);
    }
    if (isAndroid()) {
      if (localStorage.getItem(DISMISS_KEYS.playStore)) return;
      const timer = setTimeout(() => setShowStoreBanner('android'), 3000);
      return () => clearTimeout(timer);
    }

    // Desktop path: keep the existing PWA install flow via beforeinstallprompt.
    if (localStorage.getItem(DISMISS_KEYS.pwa)) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    }
    setDismissed(true);
  };

  const handleDismissPWA = () => {
    localStorage.setItem(DISMISS_KEYS.pwa, 'true');
    setDismissed(true);
  };

  const handleDismissStore = () => {
    if (showStoreBanner === 'ios') {
      localStorage.setItem(DISMISS_KEYS.appStore, 'true');
    } else if (showStoreBanner === 'android') {
      localStorage.setItem(DISMISS_KEYS.playStore, 'true');
    }
    setShowStoreBanner(null);
  };

  const openAppStore = () => {
    // Apple deep link — opens the App Store app on iOS, otherwise web
    // App Store in the browser. Universal Links re-route to the
    // installed app if present.
    window.location.href = APP_STORE_URL;
    setShowStoreBanner(null);
  };

  const openPlayStore = () => {
    // Try the market:// intent first (opens Play Store app directly).
    // If unhandled, fall back to the https URL after 600ms.
    let fallbackTimer = setTimeout(() => {
      window.location.href = PLAY_STORE_URL;
    }, 600);
    // If the user navigates away (Play Store opened), the timer is
    // effectively cancelled by the page becoming hidden.
    const onHide = () => {
      clearTimeout(fallbackTimer);
      document.removeEventListener('visibilitychange', onHide);
    };
    document.addEventListener('visibilitychange', onHide);
    window.location.href = PLAY_STORE_INTENT;
    setShowStoreBanner(null);
  };

  const showAddToHomeInstead = () => {
    // Secondary path on iOS for users who'd rather install the PWA
    // (free, no App Store account, instant). Triggered from the
    // "Use in browser" link on the App Store banner.
    setShowStoreBanner(null);
    setShowIOSAddToHome(true);
  };

  // ── iOS App Store banner (mobile Safari/Chrome iOS browser) ───────
  // Two CTAs: "Open in App" (primary — fires Universal Link, falls
  // back to App Store automatically if app isn't installed) and "Get
  // it" (secondary — direct App Store link). The Open in App probe
  // means users who already have the app installed are routed straight
  // into it; users who don't are pushed to the store transparently.
  if (showStoreBanner === 'ios' && !dismissed) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 z-50 p-4 rounded-2xl backdrop-blur-xl shadow-lg animate-in slide-in-from-bottom-4 duration-300"
        style={{ background: 'rgba(15,23,42,0.96)', border: '1px solid rgba(255,255,255,0.1)', maxWidth: '420px', margin: '0 auto' }}
        data-testid="install-prompt-appstore"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(99,102,241,0.18)' }}>
            <Smartphone className="h-5 w-5 text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">{t('generated.pwacomponents_open_eili_in_a633')}</p>
            <p className="text-xs text-slate-400 mt-0.5">{t('generated.pwacomponents_faster_with_push_684e')}</p>
          </div>
          <button onClick={handleDismissStore} className="p-1 text-slate-500 hover:text-slate-300" aria-label={t('generated.pwacomponents_dismiss_0e03')} data-testid="install-prompt-appstore-dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { openInNativeApp(); }}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white shrink-0 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
            data-testid="install-prompt-appstore-open"
          >
            <ExternalLink className="h-4 w-4" />
            {t('generated.pwacomponents_open_in_app_4d4d')}
          </button>
          <button
            onClick={openAppStore}
            className="px-3 py-2.5 rounded-xl text-xs font-medium text-slate-300 hover:text-white"
            style={{ background: 'rgba(255,255,255,0.05)' }}
            data-testid="install-prompt-appstore-store"
          >
            {t('generated.pwacomponents_get_it_cc62')}
          </button>
        </div>
        <button
          onClick={showAddToHomeInstead}
          className="w-full mt-2 py-1.5 rounded-lg text-[11px] font-medium text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="install-prompt-appstore-pwa-instead"
        >
          {t('generated.pwacomponents_or_add_to_eecc')}
        </button>
      </div>
    );
  }

  // ── Android Play Store banner (mobile Chrome/Firefox/Samsung browser) ─
  // "Open in App" uses an `intent://` URL. Chrome interprets it, opens
  // the named package if installed, otherwise navigates to the
  // browser_fallback_url (= Play Store). One tap → either app or store.
  if (showStoreBanner === 'android' && !dismissed) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 z-50 p-4 rounded-2xl backdrop-blur-xl shadow-lg animate-in slide-in-from-bottom-4 duration-300"
        style={{ background: 'rgba(15,23,42,0.96)', border: '1px solid rgba(255,255,255,0.1)', maxWidth: '420px', margin: '0 auto' }}
        data-testid="install-prompt-playstore"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(34,197,94,0.18)' }}>
            <Smartphone className="h-5 w-5 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">{t('generated.pwacomponents_open_eili_in_019e')}</p>
            <p className="text-xs text-slate-400 mt-0.5">{t('generated.pwacomponents_faster_with_push_684e')}</p>
          </div>
          <button onClick={handleDismissStore} className="p-1 text-slate-500 hover:text-slate-300" aria-label={t('generated.pwacomponents_dismiss_0e03')} data-testid="install-prompt-playstore-dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { openInNativeApp(); }}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
            data-testid="install-prompt-playstore-open"
          >
            <ExternalLink className="h-4 w-4" />
            {t('generated.pwacomponents_open_in_app_4d4d')}
          </button>
          <button
            onClick={openPlayStore}
            className="px-3 py-2.5 rounded-xl text-xs font-medium text-slate-300 hover:text-white"
            style={{ background: 'rgba(255,255,255,0.05)' }}
            data-testid="install-prompt-playstore-store"
          >
            {t('generated.pwacomponents_get_it_cc62')}
          </button>
        </div>
      </div>
    );
  }

  // ── iOS PWA "Add to Home Screen" instructions (secondary path) ────
  if (showIOSAddToHome && !dismissed) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 z-50 p-4 rounded-2xl backdrop-blur-xl shadow-lg"
        style={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.1)', maxWidth: '420px', margin: '0 auto' }}
        data-testid="ios-install-prompt"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(251,191,36,0.15)' }}>
            <Download className="h-5 w-5 text-amber-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-white">{t('generated.pwacomponents_add_eili_to_f3c8')}</p>
            <p className="text-xs text-slate-400 mt-0.5">{t('generated.pwacomponents_add_to_your_a25a')}</p>
          </div>
          <button onClick={() => { localStorage.setItem(DISMISS_KEYS.appStore, 'true'); setShowIOSAddToHome(false); setDismissed(true); }} className="p-1 text-slate-500 hover:text-slate-300" data-testid="ios-install-dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-2 pl-1">
          <div className="flex items-center gap-3 text-xs text-slate-300">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-blue-500/20 shrink-0">
              <Share className="h-3.5 w-3.5 text-blue-400" />
            </div>
            <span>{t('generated.pwacomponents_tap_the_a744')} <strong className="text-white">{t('generated.pwacomponents_share_64ef')}</strong> {t('generated.pwacomponents_button_in_safari_91c6')}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-300">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-slate-500/20 shrink-0">
              <Plus className="h-3.5 w-3.5 text-slate-400" />
            </div>
            <span>{t('generated.pwacomponents_scroll_down_and_98ae')} <strong className="text-white">{t('generated.pwacomponents_add_to_home_16ad')}</strong></span>
          </div>
        </div>
        <button
          onClick={() => { localStorage.setItem(DISMISS_KEYS.appStore, 'true'); setShowIOSAddToHome(false); setDismissed(true); }}
          className="w-full mt-4 py-2 rounded-xl text-xs font-medium text-slate-400 hover:text-white transition-colors"
          style={{ background: 'rgba(255,255,255,0.05)' }}
        >
          {t('generated.pwacomponents_maybe_later_5aae')}
        </button>
      </div>
    );
  }

  // ── Desktop PWA install (existing beforeinstallprompt flow) ───────
  if (!deferredPrompt || dismissed) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 flex items-center gap-3 p-3 rounded-2xl backdrop-blur-xl shadow-lg"
      style={{ background: 'rgba(15,23,42,0.9)', border: '1px solid rgba(255,255,255,0.1)', maxWidth: '400px', margin: '0 auto' }}
      data-testid="install-prompt"
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(251,191,36,0.15)' }}>
        <Download className="h-5 w-5 text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{t('generated.pwacomponents_install_eili_66ff')}</p>
        <p className="text-xs text-slate-400">{t('generated.pwacomponents_add_to_home_534d')}</p>
      </div>
      <button
        onClick={handleInstall}
        className="px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-900 shrink-0"
        style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)' }}
        data-testid="install-prompt-accept"
      >
        {t('generated.pwacomponents_install_58d8')}
      </button>
      <button onClick={handleDismissPWA} className="p-1 text-slate-500 hover:text-slate-300" data-testid="install-prompt-dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function OfflineIndicator() {
  const {
    t
  } = useTranslation();

  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-center py-1.5 text-xs font-medium"
      style={{ paddingTop: 'var(--safe-area-top, env(safe-area-inset-top, 0px))', background: 'rgba(234,88,12,0.95)', color: '#fff' }}
      data-testid="offline-indicator"
    >
      {t('generated.pwacomponents_you_re_offline_de0a')}
    </div>
  );
}

export function UpdateAvailablePrompt() {
  const {
    t
  } = useTranslation();

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Check for waiting SW on load
    navigator.serviceWorker.ready.then((reg) => {
      if (reg.waiting) {
        setUpdateAvailable(true);
      }
      
      // Listen for new SW waiting
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdateAvailable(true);
            }
          });
        }
      });
    });

    // Also listen for controller change (SW took over)
    const onControllerChange = () => {
      if (updating) {
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, [updating]);

  const handleUpdate = () => {
    setUpdating(true);
    navigator.serviceWorker.ready.then((reg) => {
      if (reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    });
    // Fallback: if controllerchange doesn't fire, reload after 2s
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  if (!updateAvailable) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 flex items-center gap-3 p-3 rounded-2xl backdrop-blur-xl shadow-lg animate-in slide-in-from-bottom-4 duration-300"
      style={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(99,102,241,0.3)', maxWidth: '400px', margin: '0 auto' }}
      data-testid="update-available-prompt"
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(99,102,241,0.15)' }}>
        <Download className="h-5 w-5 text-indigo-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{t('generated.pwacomponents_update_available_65d4')}</p>
        <p className="text-xs text-slate-400">{t('generated.pwacomponents_tap_to_refresh_c75f')}</p>
      </div>
      <button
        onClick={handleUpdate}
        disabled={updating}
        className="px-3 py-1.5 rounded-xl text-xs font-semibold text-white shrink-0 disabled:opacity-60"
        style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
        data-testid="update-available-refresh"
      >
        {updating ? 'Updating...' : 'Refresh'}
      </button>
    </div>
  );
}
